import { Badge, Button, Card, Group, Stack, Text, Title } from '@mantine/core';
import qrcode from 'qrcode-generator';
import { Link, useParams } from 'react-router';
import { useQrDurum } from '../api/queries';
import { PTW_DURUM_ETIKET, PTW_SUPER_DURUM_ETIKET } from '../i18n/tr';

import { QueryStatus } from './parts';
import { zamanGoster } from '../lib/format';

/**
 * QR SAYFASI (F3·S5 — T2: "izin noktasına asılan çıktıda QR: tarayan izni ve
 * güncel durumunu görür").
 *
 * ÜÇ KİLİTLİ KURAL:
 *  1. **QR YALNIZ ADRES TAŞIR** — durum verisi karede DEĞİL, sunucudadır;
 *     kare eskimez, sayfa her açılışta güncel durumu okur.
 *  2. **KİMLİKSİZ ERİŞİM YOK** — sayfa oturum ister (uç 401 verir). Karenin
 *     fotoğrafını çeken biri veriyi göremez.
 *  3. **ÖZET DAR ve KİMLİK VERİSİZ** (madde 54 ✅ — Kapsam §7.5): izin türü ·
 *     durum · geçerlilik · lokasyon. Talep eden / güncel sorumlu ADI kartta
 *     YOKTUR ve ağdan inen yanıtta da yoktur — *izin noktasındaki kişinin
 *     sorusu "bu iş şu an izinli mi", "bu işi kim yapıyor" değil.* Kimi
 *     ilgilendirdiğini görmek isteyen izin detayına gider (orada okuma
 *     kuralı işler). Kartın kimin göreceği ise müşteri yapılandırmasıdır
 *     (`qr_ozet_kapsami`) — karar sunucuda, ekran taşıyıcı.
 *
 * Kare bu ekranda ÜRETİLİR (yazdırılıp izin noktasına asılır); çıktının
 * kendisi (PDF) v1 dışıdır — PDF çıktı açık uçtur (§2.20).
 */
export function IzinQrScreen(): React.JSX.Element {
  const { permitId } = useParams();
  const durum = useQrDurum(permitId ?? null);
  const adres = `${window.location.origin}/izinler/${permitId ?? ''}/qr`;

  return (
    <Stack maw={520}>
      <Title order={3}>İzin QR kartı</Title>
      <QueryStatus queries={[durum]} />
      {durum.isSuccess && (
        <>
          <Card withBorder data-testid="qr-durum-karti">
            <Stack gap="xs">
              <Text fw={600}>
                {durum.data.workplaceAd} — {durum.data.locationAd}
              </Text>
              <Group gap="xs">
                <Badge size="lg" data-testid="qr-durum-rozeti">
                  {PTW_DURUM_ETIKET[durum.data.durum]}
                </Badge>
                <Badge variant="outline">{PTW_SUPER_DURUM_ETIKET[durum.data.superDurum]}</Badge>
              </Group>
              <Text size="sm" data-testid="qr-turler">
                İzin türü: {durum.data.izinTurleri.join(' · ') || '—'}
              </Text>
              <Text size="sm">
                Geçerlilik: {zamanGoster(durum.data.gecerlilikBaslangic)} →{' '}
                {zamanGoster(durum.data.gecerlilikBitis)}
              </Text>
            </Stack>
          </Card>

          <Card withBorder>
            <Stack gap="xs" align="center">
              <QrKaresi adres={adres} />
              <Text size="xs" c="dimmed" ta="center" data-testid="qr-adres">
                {adres}
              </Text>
              <Text size="xs" c="dimmed" ta="center">
                Kare yalnız bu sayfanın adresini taşır; durum bilgisi sunucudan okunur ve giriş
                ister.
              </Text>
              <Group>
                <Button size="xs" variant="default" onClick={() => window.print()}>
                  Yazdır
                </Button>
                <Button
                  size="xs"
                  variant="light"
                  component={Link}
                  to={`/izinler/${permitId ?? ''}/saha`}
                >
                  Saha ekranını aç
                </Button>
              </Group>
            </Stack>
          </Card>
        </>
      )}
    </Stack>
  );
}

/** Kare üretimi istemcide: sunucuya görüntü çizdirmek için sebep yok. */
function QrKaresi({ adres }: { adres: string }): React.JSX.Element {
  const qr = qrcode(0, 'M');
  qr.addData(adres);
  qr.make();
  return (
    <div
      data-testid="qr-karesi"
      aria-label="İzin QR karesi"
      // createSvgTag güvenli bir SVG üretir (girdi yalnız kendi adresimiz).
      dangerouslySetInnerHTML={{ __html: qr.createSvgTag({ cellSize: 4, margin: 2 }) }}
    />
  );
}
